import React from 'react';
import { Typography } from 'antd';
import { Minus } from 'lucide-react';

import { CreativityType, Resolutions } from '../../types/banner';
import type { RoutedFormData } from '../../types/templates';
import { LegacyAdChannels } from '../../types/ad-channels';

import { useResponsiveSizes } from '../../hooks/use-responsive-sizes';
import { useTemplateInfo } from '../../hooks/banners/use-template-utils';
import { useDynamicPreviews } from '../../hooks/use-dynamic-previews';

import { RESOLUTIONS_ORDER, RESOLUTION_CATEGORIES } from '../../constants/channels';
import { getHandlerForTemplate } from '../../handlers';

import { AdChannelTabs } from './ad-channel-tabs';
import { LegacyChannelTabs } from './legacy-channel-tabs';
import { ResolutionButtons } from './resolution-buttons';
import { PreviewRenderer } from './preview-renderer';
import { ResolutionInfo } from './resolution-info';
import { DynamicBreadcrumbs } from './dynamic-breadcrumbs';
import { tokens } from '../../theme/tokens';
import { useTemplateConfig } from '../../hooks/banners/use-template-config';

const { Title } = Typography;

interface DemoPreviewProps {
  creativityType: CreativityType;
  formData?: RoutedFormData;
}

const allResolutionsHaveCategory = (resolutions: string[]): boolean =>
  resolutions.length > 0 && resolutions.every(res => (RESOLUTION_CATEGORIES as Record<string, unknown>)[res]);

export const DemoPreview: React.FC<DemoPreviewProps> = ({ creativityType, formData = {} }) => {
  const { spacing, fontSize, screens } = useResponsiveSizes();
  const clientHandler = getHandlerForTemplate(creativityType);
  const template = useTemplateConfig(creativityType);
  const legacyTemplate = template?.mode === 'legacy' ? template : undefined;

  // Dynamic system for templates
  const {
    hasDynamicPreviews,
    previews: dynamicPreviews,
    availableChannels
  } = useDynamicPreviews(creativityType, formData, clientHandler, legacyTemplate);

  // Sistema legacy (mediaVariants)
  const { previewVideo: defaultVideo, mediaVariants, thumbnail } = useTemplateInfo(creativityType);

  // Determine which system to use
  const useDynamicSystem = hasDynamicPreviews && dynamicPreviews && Object.keys(dynamicPreviews).length > 0;
  const hasMediaVariants = Object.keys(mediaVariants).length > 0;
  const useLegacySystem = !useDynamicSystem && hasMediaVariants;

  // Available resolutions
  const resolutions = React.useMemo(() => {
    if (useDynamicSystem && dynamicPreviews) {
      return Object.keys(dynamicPreviews);
    }
    if (!hasMediaVariants) return [];

    const mediaKeys = Object.keys(mediaVariants);
    const orderedSet = new Set(mediaKeys);

    // Resolutions that are in RESOLUTIONS_ORDER come first in that order,
    // the rest are added at the end in the order they appear
    const ordered = RESOLUTIONS_ORDER.filter(res => orderedSet.has(res));
    const remaining = mediaKeys.filter(res => !RESOLUTIONS_ORDER.includes(res as Resolutions));
    return [...ordered, ...remaining];
  }, [useDynamicSystem, dynamicPreviews, hasMediaVariants, mediaVariants]);

  const useCategoryTabs = useLegacySystem && allResolutionsHaveCategory(resolutions);

  const [selectedCategory, setSelectedCategory] = React.useState<LegacyAdChannels>('social');
  const [selectedResolution, setSelectedResolution] = React.useState<string | null>(() => {
    return resolutions.length > 0 ? resolutions[0] : null;
  });

  // Get preview(s) for the selected resolution
  const selectedPreviews = React.useMemo(() => {
    if (!selectedResolution) return null;

    if (useDynamicSystem && dynamicPreviews) {
      return dynamicPreviews[selectedResolution];
    }

    if (useLegacySystem) {
      return mediaVariants[selectedResolution];
    }

    return null;
  }, [selectedResolution, useDynamicSystem, dynamicPreviews, useLegacySystem, mediaVariants]);

  // Auto-select the first available resolution when the resolutions change
  React.useEffect(() => {
    if (resolutions.length === 0) {
      setSelectedResolution(null);
      return;
    }
    if (!selectedResolution || !resolutions.includes(selectedResolution)) {
      setSelectedResolution(resolutions[0]);
    }
  }, [resolutions, selectedResolution]);

  // Function to get previews by channel (for AdChannelTabs)
  const getPreviewsForChannel = React.useCallback(
    (channel: string) => {
      const channelFormData = { ...formData, adChannel: channel };
      return clientHandler.getDemoPreviews?.(channelFormData, creativityType) || {};
    },
    [formData, clientHandler, creativityType]
  );

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'start'
      }}
    >
      {/* Header */}
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          borderBottom: '1px solid #f0f0f0'
        }}
      >
        <Title
          style={{ margin: `${spacing.sm}px 0`, fontSize: fontSize.xxl, color: tokens.colors.primary.neutral[1010] }}
        >
          Demo
        </Title>
      </div>

      <div
        style={{
          height: '100%',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          gap: spacing.xs,
          padding: screens.xs ? spacing.sm : spacing.md
        }}
      >
        {useDynamicSystem && legacyTemplate?.breadcrumbConfig && (
          <DynamicBreadcrumbs
            formData={formData}
            config={legacyTemplate.breadcrumbConfig}
            separator={<Minus width={screens.xs ? 10 : 15} />}
          />
        )}

        {/* Dynamic System Tabs*/}
        {useDynamicSystem && legacyTemplate?.channelConfigs && availableChannels.length > 0 && (
          <AdChannelTabs
            selectedResolution={selectedResolution}
            onResolutionChange={setSelectedResolution}
            availableChannels={availableChannels}
            channelConfigs={legacyTemplate.channelConfigs}
            getPreviewsForChannel={getPreviewsForChannel}
          />
        )}

        {/* Sistema legacy w/ categories: Tabs Social/Display/DOOH */}
        {useCategoryTabs && (
          <LegacyChannelTabs
            resolutions={resolutions}
            selectedResolution={selectedResolution}
            onResolutionChange={setSelectedResolution}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
          />
        )}

        {/* Flat resolution buttons for templates whose resolutions don't fit into legacy categories */}
        {useLegacySystem && !useCategoryTabs && resolutions.length > 1 && (
          <ResolutionButtons
            resolutions={resolutions}
            selectedResolution={selectedResolution}
            onResolutionChange={setSelectedResolution}
          />
        )}

        {/* Preview Renderer */}
        <PreviewRenderer previews={selectedPreviews || defaultVideo} thumbnail={thumbnail} />

        {/* Resolution Info */}
        {useLegacySystem && selectedPreviews && useCategoryTabs && <ResolutionInfo resolution={selectedResolution} />}
      </div>
    </div>
  );
};
